import { useState } from 'react'

export type Presence = 'entering' | 'leaving'

/**
 * Keeps items that left `items` rendered as 'leaving' until `settle` drops
 * them, and marks items that arrived in the latest change as 'entering'.
 * With `animate` off, changes apply at once.
 */
export function useListPresence<T extends { id: string }>(
  items: T[],
  animate: boolean
) {
  const [prev, setPrev] = useState(items)
  const [entering, setEntering] = useState<ReadonlySet<string>>(new Set())
  const [leaving, setLeaving] = useState<{ item: T; index: number }[]>([])

  if (items !== prev) {
    setPrev(items)
    const before = new Set(prev.map((i) => i.id))
    const now = new Set(items.map((i) => i.id))
    setEntering(
      new Set(animate ? [...now].filter((id) => !before.has(id)) : [])
    )
    setLeaving((current) => [
      ...current.filter((l) => !now.has(l.item.id)),
      ...(animate
        ? prev.flatMap((item, index) =>
            now.has(item.id) ? [] : [{ item, index }]
          )
        : []),
    ])
  }

  // Rows that mount later, as when a collapsed list reopens, must not replay.
  if (!animate && (entering.size > 0 || leaving.length > 0)) {
    setEntering(new Set())
    setLeaving([])
  }

  const rows: { item: T; presence?: Presence }[] = items.map((item) => ({
    item,
    presence: entering.has(item.id) ? 'entering' : undefined,
  }))
  for (const { item, index } of leaving) {
    rows.splice(index, 0, { item, presence: 'leaving' })
  }

  const settle = (id: string) =>
    setLeaving((current) => current.filter((l) => l.item.id !== id))
  const settleAll = () => setLeaving([])

  return { rows, settle, settleAll }
}
